import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { PLAN_LEVEL } from "@/mock";
import { logError } from "@/lib/logger";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth relies on the httpOnly `session_token` cookie set by the backend (not readable by JS).
axios.defaults.withCredentials = true;
// Clean up tokens persisted by older versions of the app.
localStorage.removeItem("douceur_token");

export const getAnonUid = () => {
  let id = localStorage.getItem("douceur_uid");
  if (!id) {
    id = window.crypto?.randomUUID?.() || `u-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("douceur_uid", id);
  }
  return id;
};

// The server binds this device's anonymous uid to an httpOnly cookie; it may hand back a different uid.
let anonPromise = null;
export const ensureAnon = () => {
  if (!anonPromise) {
    anonPromise = axios.post(`${API}/auth/anon`, { uid: localStorage.getItem("douceur_uid") })
      .then(({ data }) => {
        localStorage.setItem("douceur_uid", data.uid);
        return data.uid;
      })
      .catch((e) => {
        anonPromise = null;
        throw e;
      });
  }
  return anonPromise;
};

axios.interceptors.request.use(async (config) => {
  const m = config.url?.match(/\/api\/users\/([^/]+)/);
  if (m && !m[1].startsWith("user_")) {
    const uid = await ensureAnon();
    if (uid !== m[1]) config.url = config.url.replace(`/users/${m[1]}`, `/users/${uid}`);
  }
  return config;
});

export const errMsg = (e, fallback = "Une erreur est survenue") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return fallback;
};

const Ctx = createContext(null);

// Auth actions only depend on stable state setters, so they are created once (outside render).
function createAuthActions(setUser, setChecking) {
  const finishLogin = (data) => {
    setUser(data.user);
    return data.user;
  };
  const post = async (path, body) => {
    const anon_uid = await ensureAnon().catch(() => null);
    return finishLogin((await axios.post(`${API}${path}`, { ...body, anon_uid })).data);
  };

  return {
    refresh: async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        setUser(data);
        return data;
      } catch (e) {
        if (e?.response?.status !== 401) logError("Auth refresh failed", e);
        setUser(null);
        return null;
      } finally {
        setChecking(false);
      }
    },
    login: (email, password) => post("/auth/login", { email, password }),
    register: (name, email, password) => post("/auth/register", { name, email, password }),
    verifyEmail: (token) => axios.post(`${API}/auth/verify-email`, { token }),
    resendVerification: () => axios.post(`${API}/auth/verify-email/resend`),
    googleExchange: (session_id) => post("/auth/google/session", { session_id }),
    googleLogin: (next = "/") => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + next;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    },
    logout: async () => {
      try {
        await axios.post(`${API}/auth/logout`);
      } catch (e) {
        logError("Logout request failed", e);
      }
      setUser(null);
    },
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const actions = useMemo(() => createAuthActions(setUser, setChecking), []);

  useEffect(() => {
    // CRITICAL: if returning from Google OAuth, skip /me — AuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    actions.refresh();
  }, [actions]);

  const value = useMemo(() => {
    const plan = user?.plan || "free";
    const hasPlan = (p) => PLAN_LEVEL[plan] >= PLAN_LEVEL[p];
    return { user, checking, plan, hasPlan, ...actions };
  }, [user, checking, actions]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
